"use client";
import { KeyboardEvent, ReactNode } from "react";
import HorizontalScroller from "./horizontal-scroller";

type Tab<T extends string> = {
  label: ReactNode;
  value: T;
};

type TabBarProps<T extends string> = {
  tabs: Tab<T>[];
  active: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  /**
   * Set when the tabs control panels rendered with `tabPanelProps`. Turns the
   * bar into an ARIA tablist with arrow-key navigation between tabs.
   */
  idPrefix?: string;
};

export const tabId = (prefix: string, value: string) => `${prefix}-tab-${value}`;
export const panelId = (prefix: string, value: string) =>
  `${prefix}-panel-${value}`;

/** Props for a panel controlled by a TabBar with `idPrefix`. */
export const tabPanelProps = (prefix: string, value: string, active: string) => ({
  role: "tabpanel",
  id: panelId(prefix, value),
  "aria-labelledby": tabId(prefix, value),
  hidden: value !== active,
});

export function TabBar<T extends string>({
  tabs,
  active,
  onChange,
  disabled,
  idPrefix,
}: TabBarProps<T>) {
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!idPrefix) return;
    const index = tabs.findIndex((t) => t.value === active);
    const next =
      event.key === "ArrowRight" ? (index + 1) % tabs.length
      : event.key === "ArrowLeft" ? (index - 1 + tabs.length) % tabs.length
      : event.key === "Home" ? 0
      : event.key === "End" ? tabs.length - 1
      : -1;
    if (next < 0) return;
    event.preventDefault();
    onChange(tabs[next].value);
    document.getElementById(tabId(idPrefix, tabs[next].value))?.focus();
  };

  return (
    <HorizontalScroller
      // The base line lives on the wrapper so it runs unbroken under the arrows,
      // where the scrolling content is masked out.
      className="w-full min-w-0 mb-4 after:absolute after:h-[4px] after:bg-sand-700 after:bottom-0 after:inset-x-0"
      size="sm"
      activeSelector="[data-active='true']"
      activeKey={active}
    >
      <div className="flex whitespace-nowrap" role={idPrefix ? "tablist" : undefined}>
        {tabs.map((tab) => {
          const isActive = active === tab.value;
          const ariaProps = idPrefix
            ? {
                role: "tab",
                id: tabId(idPrefix, tab.value),
                "aria-selected": isActive,
                "aria-controls": panelId(idPrefix, tab.value),
                tabIndex: isActive ? 0 : -1,
              }
            : {};

          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => onChange(tab.value)}
              onKeyDown={onKeyDown}
              disabled={disabled || tabs.length === 1}
              data-active={isActive}
              {...ariaProps}
              className={`relative after:z-10 after:absolute after:h-[4px] after:w-full after:bottom-0 after:inset-x-0 font-bold px-3.5 py-2.5 transition-colors ${
                isActive
                  ? "after:bg-red-700"
                  : "after:bg-sand-700 hover:after:bg-sand-900 text-gray-500 hover:text-black"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </HorizontalScroller>
  );
}
